<template>
  <!--
    审批表单（只读）：按后端返回的字段列表渲染业务单据内容

    数据驱动而非组件注册表 —— 业务把单据整理成「标签 + 值 + 类型」返回，
    这一套组件渲染所有业务，新业务接入不需要改任何前端代码。
    字段的展示顺序由后端决定（列表有序），格式化的知识也在业务侧，
    前端只按 type 决定用什么排版。
  -->
  <div class="approval-form">
    <div v-if="!fields || !fields.length" class="form-empty">
      <yq-icon name="element-ui_info"/>
      <span>{{ emptyText }}</span>
    </div>
    <template v-else>
      <!--
        用项目标准的 form-items / form-item 组合，而不是 el-form + el-row。

        为什么不用 el-row：它靠负 margin 抵消列间距，在弹框这种带内边距的容器里
        会让内容溢出，产生横向滚动条。

        form-items 用 flex-wrap + calc(百分比 - 间距)，自身不溢出；
        form-item 的 colSpan 就是跨列，语义与「哪些字段要占整行」正好对应。
      -->
      <form-items :data="{}" :columns="columns" label-width="2.4rem" icon-width="0" class="form-body">
        <form-item v-for="(field, idx) in fields" :key="idx"
                   :label="field.label"
                   :col-span="isFullRow(field) ? columns : 1" :align-items="alignItems(field.type)">
          <!-- 子表：主从表的明细 -->
          <div v-if="field.type === 'table'">
            <yq-table :data="tableRows(field)" :empty-text="'无明细'" height="auto" size="small">
              <yq-table-column v-for="(col, ci) in (field.columns || [])" :key="ci"
                               :label="col" :prop="'c' + ci" min-width="110rem"
                               :show-overflow-tooltip="true"/>
            </yq-table>
          </div>
          <!--
            附件：直接复用 image-upload，disabled 模式下只读展示、点击可预览。
            不自己解析 URL 再渲染 —— 组件内部已经处理了三种存储格式
            （JSON 数组 / 单对象 / 逗号串）与域名拼接，重复实现一遍就是多一处出错点。
          -->
          <image-upload v-else-if="field.type === 'image'"
                        :model-value="field.value || ''"
                        :disabled="true"
                        :limit="20"/>
          <el-tag v-else-if="field.type === 'tag'" size="small" type="info">{{ field.value || '—' }}</el-tag>
          <span v-else-if="field.type === 'money'" class="field-money field-value">{{ fmtMoney(field.value) }}</span>
          <span v-else-if="field.type === 'date'" class="field-num field-value">{{ dateOnly(field.value) }}</span>
          <span v-else-if="field.type === 'datetime'" class="field-num field-value">{{ fmtTime(field.value) }}</span>
          <span v-else :class="valueClass(field)" class="field-value">{{ field.value || '—' }}</span>
        </form-item>
      </form-items>
    </template>
  </div>
</template>

<script>
import YqTable from "@/components/h5/table/YqTable.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import ImageUpload from "@/components/h5/image/ImageUpload.vue";

export default {
  name: "ApprovalForm",
  components: {ImageUpload, FormItem, FormItems, YqIcon, YqTableColumn, YqTable},
  props: {
    /** 字段列表，由 /basedata/process/flow/instance/queryBusinessDetail 返回 */
    fields: {default: () => []},
    emptyText: {default: "该业务未配置单据详情展示"},
    /** 每行显示几个字段。超出后自动换行；长内容仍占整行 */
    columns: {default: 2},
  },
  methods: {
    /**
     * 是否占整行。
     *
     * <p>三类必须独占一行：明细表（列多，挤在半列里没法看）、
     * 附件（图要够大才看得清）、长文本（换行后与邻列高度不齐，布局会很难看）。
     */
    isFullRow(field) {
      return field.type === "table"
        || field.type === "image"
        || field.type === "textarea"
        || field.fullWidth === true;
    },
    alignItems(type) {
      return (type === "table" || type === "textarea")? 'flex-start': 'center';
    },
    /** 把后端的行数组转成对象数组，交给 yq-table 按索引取列 */
    tableRows(field) {
      return (field.rows || []).map(row => {
        const obj = {};
        (row || []).forEach((cell, i) => {
          obj["c" + i] = cell;
        });
        return obj;
      });
    },
    valueClass(field) {
      if (field.type === "money") {
        return "field-money block-value";
      }
      return "field-text block-value";
    },
    fmtMoney(v) {
      if (v === null || v === undefined || v === "") {
        return "—";
      }
      const n = Number(v);
      if (isNaN(n)) {
        return v;
      }
      // 金额固定两位小数、千分位 —— 后端给的是裸数值，排版交给前端统一
      return n.toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    dateOnly(v) {
      return v ? String(v).replace("T", " ").slice(0, 10) : "—";
    },
    fmtTime(v) {
      return v ? String(v).replace("T", " ").slice(0, 16) : "—";
    },
  },
};
</script>

<style lang="scss" scoped>
.approval-form {
  /*
    form-item 默认给 form-value 设了 line-height: 32px（为单行文本对齐标签用的）。
    表格与附件图片是多行内容，沿用这个行高会在每张图/表格上下撑出多余的空白，
    所以带这些内容的字段把行高还原成正常值。
  */
  :deep(.form-item) .form-value {
    overflow: hidden;
  }

  .form-body :deep(.image-upload),
  .form-body :deep(.yq-table) {
    line-height: normal;
  }

  .field-text {
    color: #303133;
  }

  .field-money {
    color: #303133;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .field-num {
    color: #606266;
    font-variant-numeric: tabular-nums;
  }
  .field-value{
    width: 100%;
    display: inline-block;
    box-sizing: border-box;
    padding: 0 10px;
    border-radius: 4px;
    border: 1px solid var(--yq-border-color);
    line-height: 32px;
    background: var(--yq-background-color);
  }

  .block-value {
    //line-height: 1.7;
    white-space: pre-wrap;
    word-break: break-all;
  }

  .muted {
    color: #909399;
  }

  .form-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #909399;
    font-size: 13px;
    background: #fafbfc;
    border: 1px dashed var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 18px 14px;
  }
}
</style>
